import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { inTextField } from '../keyboard';
import './ShortcutsHelp.css';

/** Cross-platform modifier label (we never assume Mac vs PC). */
const MOD = 'Ctrl / ⌘';

interface Shortcut {
  /** Keys shown as adjacent <kbd> chips; the description clarifies combo vs set. */
  keys: string[];
  desc: string;
}

interface Group {
  title: string;
  items: Shortcut[];
}

/**
 * The verified shortcut set (cue-forge-9ww.4). Each entry maps to a real handler:
 * the rail tablist (Rail.tsx), Editor undo/redo (Editor.tsx), and the cue grid
 * (CueTable.tsx). Keep this in sync when shortcuts change.
 */
const GROUPS: Group[] = [
  {
    title: 'Navigation',
    items: [
      { keys: ['↑', '↓'], desc: 'Switch modes (when the rail is focused)' },
      { keys: ['Home', 'End'], desc: 'Jump to the first / last mode' },
    ],
  },
  {
    title: 'Editing',
    items: [
      { keys: [MOD, 'Z'], desc: 'Undo' },
      { keys: [MOD, 'Y'], desc: 'Redo (also ' + MOD + ' + Shift + Z)' },
    ],
  },
  {
    title: 'Cue grid',
    items: [
      { keys: ['↑', '↓', '←', '→'], desc: 'Move between cells' },
      { keys: ['Enter'], desc: 'Edit the focused cell (also Space or F2)' },
      { keys: ['Enter'], desc: 'Commit a time edit' },
      { keys: [MOD, 'Enter'], desc: 'Commit a multi-line text edit' },
      { keys: ['Esc'], desc: 'Cancel the current edit' },
    ],
  },
  {
    title: 'QC issues',
    items: [
      { keys: ['Alt', '↓'], desc: 'Next issue' },
      { keys: ['Alt', '↑'], desc: 'Previous issue' },
      { keys: ['Alt', 'Enter'], desc: 'Fix the current issue and advance' },
      {
        keys: ['Alt', 'Shift', 'Enter'],
        desc: 'Accept the current issue as intentional and advance',
      },
    ],
  },
  {
    title: 'General',
    items: [{ keys: ['?'], desc: 'Show this shortcuts help' }],
  },
];

const FOCUSABLE = 'button, a[href], input, [tabindex]:not([tabindex="-1"])';

function ShortcutsDialog({ onClose }: { onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => closeRef.current?.focus(), []);

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="cf-modal" onClick={onClose}>
      <div
        ref={panelRef}
        className="cf-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cf-shortcuts-title"
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cf-modal__head">
          <h2 id="cf-shortcuts-title" className="cf-modal__title">
            Keyboard shortcuts
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="cf-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            {'×'}
          </button>
        </div>
        <div className="cf-shortcuts">
          {GROUPS.map((group) => (
            <section key={group.title} className="cf-shortcuts__group">
              <h3 className="cf-shortcuts__heading">{group.title}</h3>
              <dl className="cf-shortcuts__list">
                {group.items.map((item, i) => (
                  <div key={i} className="cf-shortcuts__row">
                    <dt className="cf-shortcuts__keys">
                      {item.keys.map((k, j) => (
                        <kbd key={j}>{k}</kbd>
                      ))}
                    </dt>
                    <dd className="cf-shortcuts__desc">{item.desc}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Discoverable keyboard-shortcuts reference (cue-forge-9ww.4): a trigger button
 * (also opened with "?") and an accessible modal listing every app shortcut.
 * Self-contained so it can drop into the shell and be tested in isolation.
 */
export function ShortcutsHelp() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '?' && !inTextField(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="cf-shortcuts-trigger"
        aria-keyshortcuts="?"
        title="Keyboard shortcuts (?)"
        onClick={() => setOpen(true)}
      >
        <span aria-hidden="true">{'⌨'}</span>
        <span className="cf-visually-hidden">Keyboard shortcuts</span>
        {/* Visible on hover AND keyboard focus (title= only reaches mouse
            hover, after a delay, and never keyboard/touch users) so the
            floating icon has a real affordance at rest. */}
        <span className="cf-shortcuts-trigger__tip" aria-hidden="true">
          Keyboard shortcuts (?)
        </span>
      </button>
      {open && <ShortcutsDialog onClose={close} />}
    </>
  );
}
